import * as React from 'react';
import {useDispatch} from 'react-redux';
import {useQueryClient} from '@tanstack/react-query';

import {
  Toast,
  ToastBody,
  ToastTitle,
  toastApi,
} from '@spaced-out/ui-design-system/lib/components/Toast';

import {useApiQuery} from 'src/hooks/useApiQuery';
import {useApiMutation} from 'src/hooks/useApiMutation';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import * as reduxApi from 'src/utils/redux-api-v2';
import {getApiErrorMessage} from 'src/utils/errors';

import {BYOD_API_BASE as BASE} from 'src/components/analytics-genesis/byod/api';
import {
  byodQueryDefaults,
  reportByodApiError,
} from 'src/components/analytics-genesis/byod/api/error-handling';

import type {
  ByodScheduleCreateRequest,
  ByodScheduleCreateResponse,
  ByodScheduleDeleteResponse,
  ByodScheduleGetResponse,
  ByodScheduleUpdateRequest,
  ByodScheduleUpdateResponse,
} from './schedule.types';


export type UseByodScheduleResult = {
  /** The caller's active schedule for this resource, or null if none exists. */
  schedule: ByodScheduleGetResponse['schedule'];
  /** True while the initial GET is in flight (panel shows its loader). */
  isLoading: boolean;
  /**
   * True when the GET failed. Consumers MUST NOT offer create/edit on a load
   * error: an errored GET is not the same as `{schedule: null}`, and — per the
   * API contract — the placement endpoint has no 409 backstop, so treating
   * "unknown" as "no schedule" would risk a duplicate POST.
   */
  isLoadError: boolean;
  /** True while the delete mutation specifically is in flight. */
  isDeleting: boolean;
  createSchedule: (
    req: ByodScheduleCreateRequest,
  ) => Promise<ByodScheduleCreateResponse>;
  updateSchedule: (
    req: ByodScheduleUpdateRequest,
  ) => Promise<ByodScheduleUpdateResponse>;
  deleteSchedule: () => Promise<ByodScheduleDeleteResponse>;
};

function showSuccessToast(title: string, body: string): void {
  toastApi.show(
    <Toast semantic="success">
      <ToastTitle>{title}</ToastTitle>
      <ToastBody>{body}</ToastBody>
    </Toast>,
  );
}

function showDangerToast(title: string, body: string): void {
  toastApi.show(
    <Toast semantic="danger">
      <ToastTitle>{title}</ToastTitle>
      <ToastBody>{body}</ToastBody>
    </Toast>,
  );
}

// Prefer the server-provided error message (validation detail, timezone
// rejection, etc.) for the toast body, falling back to a generic string when
// the backend didn't send one. Mirrors how sibling BYOD mutations surface
// `getApiErrorMessage` rather than a blanket failure message.
function serverErrorBody(error: unknown, fallback: string): string {
  return (error instanceof Error && getApiErrorMessage(error)) || fallback;
}

// Shared GET + create/update/delete wiring for a single BYOD schedule resource.
// `basePath` is the resource's `/schedule` endpoint (widget or placement), or
// null when no target is selected (GET stays disabled). `enabled` additionally
// gates the GET so the panel only fetches when opened.
function useByodScheduleResource(
  basePath: string | null,
  queryKey: (string | null)[],
  options?: {enabled?: boolean},
): UseByodScheduleResult {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const {t} = useLabelTranslation();

  const enabled = Boolean(basePath) && (options?.enabled ?? true);

  const {data, isLoading, isError, error} =
    useApiQuery<ByodScheduleGetResponse>(
      basePath ? {pathname: basePath} : null,
      {
        queryOptions: {
          ...byodQueryDefaults,
          queryKey,
          enabled,
          // The schedule can change out from under us (another session); always
          // read fresh when the panel opens.
          staleTime: 0,
          gcTime: 0,
          refetchOnWindowFocus: false,
        },
      },
    );

  // Surface a failed GET (useApiQuery rethrows without reporting). Without this
  // the error is invisible to Sentry and — worse — a null `data` would look
  // identical to "no schedule exists", so consumers must not treat a load
  // error as an empty result (see `isLoadError`).
  const loadError = enabled && isError;
  React.useEffect(() => {
    if (loadError) {
      reportByodApiError(error, {action: 'load schedule'});
      showDangerToast(
        t('Failed_to_load_schedule', 'Failed to load schedule'),
        serverErrorBody(
          error,
          t(
            'An_error_occurred_while_loading_the_schedule',
            'An error occurred while loading the schedule',
          ),
        ),
      );
    }
  }, [loadError, error, t]);

  const invalidate = React.useCallback(
    () => queryClient.invalidateQueries({queryKey}),
    [queryClient, queryKey],
  );

  const createMutation = useApiMutation<
    ByodScheduleCreateResponse,
    Error,
    ByodScheduleCreateRequest
  >({
    mutationFn: (payload) =>
      dispatch(
        reduxApi.post(basePath as string, payload),
      ) as Promise<ByodScheduleCreateResponse>,
    onSuccess: async () => {
      showSuccessToast(
        t('Schedule_created_successfully', 'Schedule created successfully'),
        t(
          'The_schedule_has_been_created_successfully',
          'The schedule has been created successfully',
        ),
      );
      await invalidate();
      return undefined;
    },
    onError: (error) => {
      // BYOD scheduling is create-only from the table — there's no 409 special
      // case. Any create failure surfaces a generic error toast and reports to
      // Sentry. Existing schedules are managed from Settings → Reports.
      reportByodApiError(error, {action: 'create schedule'});
      showDangerToast(
        t('Failed_to_create_schedule', 'Failed to create schedule'),
        serverErrorBody(
          error,
          t(
            'An_error_occurred_while_creating_the_schedule',
            'An error occurred while creating the schedule',
          ),
        ),
      );
      return undefined;
    },
  });

  const updateMutation = useApiMutation<
    ByodScheduleUpdateResponse,
    Error,
    ByodScheduleUpdateRequest
  >({
    mutationFn: (payload) =>
      dispatch(
        reduxApi.put(basePath as string, payload),
      ) as Promise<ByodScheduleUpdateResponse>,
    onSuccess: async () => {
      showSuccessToast(
        t('Schedule_updated_successfully', 'Schedule updated successfully'),
        t(
          'The_schedule_has_been_updated_successfully',
          'The schedule has been updated successfully',
        ),
      );
      await invalidate();
      return undefined;
    },
    onError: (error) => {
      reportByodApiError(error, {action: 'update schedule'});
      showDangerToast(
        t('Failed_to_update_schedule', 'Failed to update schedule'),
        serverErrorBody(
          error,
          t(
            'An_error_occurred_while_updating_the_schedule',
            'An error occurred while updating the schedule',
          ),
        ),
      );
      return undefined;
    },
  });

  const deleteMutation = useApiMutation<
    ByodScheduleDeleteResponse,
    Error,
    void
  >({
    mutationFn: () =>
      dispatch(
        reduxApi.del(basePath as string),
      ) as Promise<ByodScheduleDeleteResponse>,
    onSuccess: async () => {
      showSuccessToast(
        t('Schedule_deleted_successfully', 'Schedule deleted successfully'),
        t(
          'The_schedule_has_been_deleted_successfully',
          'The schedule has been deleted successfully',
        ),
      );
      await invalidate();
      return undefined;
    },
    onError: (error) => {
      reportByodApiError(error, {action: 'delete schedule'});
      showDangerToast(
        t('Failed_to_delete_schedule', 'Failed to delete schedule'),
        serverErrorBody(
          error,
          t(
            'An_error_occurred_while_deleting_the_schedule',
            'An error occurred while deleting the schedule',
          ),
        ),
      );
      return undefined;
    },
  });

  return {
    schedule: data?.schedule ?? null,
    isLoading: enabled && isLoading,
    isLoadError: loadError,
    isDeleting: deleteMutation.isPending,
    createSchedule: createMutation.mutateAsync,
    updateSchedule: updateMutation.mutateAsync,
    deleteSchedule: () => deleteMutation.mutateAsync(),
  };
}

// Reports Library (standalone widget) scheduling — keyed by widget_key. The
// ByodTable entry point is create-only: each save POSTs a new schedule and no
// 409 conflict is handled here. Existing schedules are managed from
// Settings → Reports.
export function useByodWidgetSchedule(
  widgetKey: string | null,
  options?: {enabled?: boolean},
): UseByodScheduleResult {
  const basePath = widgetKey
    ? `${BASE}/widgets/${encodeURIComponent(widgetKey)}/schedule`
    : null;
  const queryKey = React.useMemo(
    () => ['byod-widget-schedule', widgetKey],
    [widgetKey],
  );
  return useByodScheduleResource(basePath, queryKey, options);
}

// Dashboard placement scheduling — keyed by placement_id
// (AnalyticsCustomDashboardWidget.id). Per the API contract: one schedule per
// placement, shared across agents, and the backend does NOT enforce uniqueness
// on POST — so callers must GET first (this hook does, gated on `enabled`) and
// only create when `schedule` is null.
export function useByodPlacementSchedule(
  placementId: string | null,
  options?: {enabled?: boolean},
): UseByodScheduleResult {
  const basePath = placementId
    ? `${BASE}/placements/${encodeURIComponent(placementId)}/schedule`
    : null;
  const queryKey = React.useMemo(
    () => ['byod-placement-schedule', placementId],
    [placementId],
  );
  return useByodScheduleResource(basePath, queryKey, options);
}
